import React from 'react';
import {
  Box,
  Divider,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import DOMPurify from 'dompurify';

/**
 * Componente para renderizar una pregunta de tipo fecha en una encuesta
 * 
 * @param {Object} props
 * @param {number} props.questionId - ID único de la pregunta
 * @param {string} props.typeQuestion - Tipo de la pregunta
 * @param {string} props.questionName - Texto de la pregunta
 * @param {string} [props.description] - Descripción adicional de la pregunta
 * @param {number} props.index - Índice de la pregunta en el array
 * @param {Array<number>} props.unansweredQuestions - Array de índices de preguntas sin responder
 * @param {Object} props.questionRef - Ref para scroll automático
 * @param {*} props.value - Valor actual del campo de fecha
 * @param {Function} props.onChange - Callback cuando cambia la fecha
 * @param {string} [props.primaryColor='#03aae4'] - Color primario del tema
 * @param {boolean} [props.showDivider=true] - Si se debe mostrar el divisor al final
 */
const DateQuestion = ({
  questionId,
  typeQuestion,
  questionName,
  description,
  index,
  unansweredQuestions = [],
  questionRef,
  value,
  onChange,
  primaryColor = '#03aae4',
  showDivider = true,
  hasOwnBold,
}) => {
  const hasError = unansweredQuestions.includes(index);

  return (
    <>
      <FormLabel
        id={`${questionId}-${typeQuestion}`}
        style={{
          fontSize: '1.1',
          fontWeight: hasOwnBold(questionName) ? 'normal' : 'bold',
          marginBottom: '0.8em',
          color: hasError ? 'red' : 'rgba(0, 0, 0, 0.6)',
        }}
        ref={questionRef}
      >
          <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(questionName) }} />
      </FormLabel>

      {description && (
        <Typography
          variant="caption"
          style={{ display: 'block', fontStyle: 'italic' }}
        >
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
        </Typography>
      )}

      <Box sx={{ marginTop: '10px' }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={3}>
            <DesktopDatePicker
              label="Ingresa la fecha por favor"
              inputFormat="MM/DD/YYYY"
              value={value || null}
              onChange={(newValue) => onChange(newValue, index)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'rgba(0, 0, 0, 0.87)',
                  '& fieldset': {
                    borderColor: hasError 
                      ? 'red' 
                      : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover fieldset': {
                    borderColor: hasError 
                      ? 'red' 
                      : 'rgba(0, 0, 0, 0.87)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: hasError 
                      ? 'red' 
                      : (primaryColor || '#03aae4'),
                  },
                },
                '& .MuiInputLabel-root': {
                  color: hasError 
                    ? 'red' 
                    : 'rgba(0, 0, 0, 0.6)',
                  '&.Mui-focused': {
                    color: hasError 
                      ? 'red' 
                      : (primaryColor || '#03aae4'),
                  },
                },
                '& .MuiSvgIcon-root': {
                  color: hasError 
                    ? 'red' 
                    : 'rgba(0, 0, 0, 0.6)',
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiSvgIcon-root': {
                  color: hasError 
                    ? 'red' 
                    : (primaryColor || '#03aae4'),
                },
              }}
              renderInput={(params) => (
                <TextField 
                  {...params}
                  error={hasError}
                  helperText={hasError ? 'Este campo es requerido' : ''}
                />
              )}
            />
          </Stack>
        </LocalizationProvider>
      </Box>

      {showDivider && <Divider variant="middle" />}
    </>
  );
};

export default DateQuestion;